import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { math } from 'polished';
import { devWarning, utilStyles, ZINDEX_DROPDOWN, highlightMatch, KeyboardCode, ScreenReaderContent } from '@paypalcorp/pp-react-utils';
import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useState, useRef, useEffect, forwardRef } from 'react';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { ContextualAlert } from '@paypalcorp/pp-react-contextual-alert';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover,
      sysSizeMediaSm = tokens.sysSizeMediaSm,
      sysSizeMediaMd = tokens.sysSizeMediaMd,
      sysSizeMediaLg = tokens.sysSizeMediaLg,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSizeFieldLg = tokens.sysSizeFieldLg,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSpaceXs = tokens.sysSpaceXs;

  var _utilStyles = utilStyles(tokens),
      elevationStyles = _utilStyles.elevationStyles;

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: auto-suggest_base;\n    position: relative;\n  "])));
  var suggestionListContainer = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: suggestion_list_container;\n    min-width: ", ";\n    max-width: ", ";\n    margin-top: ", ";\n    padding: ", " 0;\n    overflow-x: hidden;\n    width: 100%;\n  "])), math("2 * ".concat(sysSizeFieldLg)), sysSizeMediaLg, math("".concat(sysSpaceXs, " + ").concat(sysSpaceTiny)), sysSpaceSm);
  var elevatedSuggestionListContainer = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: elevated_suggestion_list_container;\n    ", "\n    ", "\n\n    border-radius: ", ";\n    background-color: ", ";\n    z-index: ", ";\n    outline: ", " solid transparent;\n    top: ", ";\n    position: absolute;\n  "])), suggestionListContainer, elevationStyles.level3, sysBorderRadiusLg, sysColorBackgroundSurface, ZINDEX_DROPDOWN, sysBorderThicknessNormal, sysSizeFieldLg);
  var content = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: content;\n    padding: ", " ", ";\n  "])), sysSpaceMd, sysSpaceLg);
  var centeredContent = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: centered_content;\n    display: flex;\n    justify-content: center;\n  "])));
  var suggestionSublist = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: suggestion_sublist;\n    list-style-type: none;\n    margin: 0;\n    padding: 0;\n  "])));
  var suggestionList = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: suggestion_list;\n    ", "\n    overflow-y: auto;\n    max-height: ", ";\n\n    @media (max-width: ", ") {\n      max-height: ", ";\n    }\n  "])), suggestionSublist, sysSizeMediaSm, sysSizeMediaMd, math("".concat(sysSizeMediaSm, " / 2")));
  var highlightedSuggesiton = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: highlighted_suggestion;\n    background-color: ", ";\n  "])), sysColorBackgroundSurfaceHover);
  var suggestion = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: suggestion;\n    ", "\n    cursor: pointer;\n    display: flex;\n    align-items: center;\n  "])), content);
  var errorMessage = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: error_message;\n    margin: 0 ", ";\n  "])), sysSpaceLg);
  var suggestionDecoration = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: suggestion_decoration;\n    margin-right: ", ";\n\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: ", ";\n    }\n  "])), sysSpaceMd, sysSpaceMd);
  return {
    base: base,
    suggestionListContainer: suggestionListContainer,
    elevatedSuggestionListContainer: elevatedSuggestionListContainer,
    content: content,
    centeredContent: centeredContent,
    suggestionSublist: suggestionSublist,
    suggestionList: suggestionList,
    highlightedSuggesiton: highlightedSuggesiton,
    suggestion: suggestion,
    errorMessage: errorMessage,
    suggestionDecoration: suggestionDecoration
  };
}

var _excluded = ["suggestions", "renderSuggestion", "onHighlight", "onSelect", "className", "listItemClassName", "query", "enableKeyboardInteraction", "hasSections"];

var defaultRenderSuggestion = function (_ref, query, _styles) {
  var value = _ref.value,
      primaryText = _ref.primaryText,
      secondaryText = _ref.secondaryText,
      decoration = _ref.decoration;
  var highlightedValue = highlightMatch(primaryText || value, query);
  var secondaryElm = secondaryText ? jsx(CaptionText, {
    "data-ppui": "true"
  }, secondaryText) : null;
  var decorationElm = decoration ? jsx("div", {
    css: _styles.suggestionDecoration,
    "data-ppui": "true"
  }, decoration) : null;
  return jsx(React.Fragment, null, decorationElm, jsx("div", {
    "data-ppui": "true"
  }, jsx(BodyText, {
    dangerouslySetInnerHTML: {
      __html: highlightedValue
    },
    "data-ppui": "true"
  }), secondaryElm));
};
/* Create a flat list of suggestions for highlight and selection by index */


var normalizeSuggestions = function (suggestions, hasSections) {
  if (!hasSections) {
    return suggestions.reduce(function (acc, suggestion, index) {
      acc.push({
        value: suggestion,
        suggestionIndex: index
      });
      return acc;
    }, []);
  }

  return suggestions.reduce(function (acc, section, sectionIndex) {
    if (section.suggestions) {
      section.suggestions.forEach(function (suggestion, suggestionIndex) {
        acc.push({
          value: suggestion,
          section: section,
          suggestionIndex: suggestionIndex,
          sectionIndex: sectionIndex
        });
      });
    }

    return acc;
  }, []);
};

var SuggestionList = function (_ref2) {
  var _ref2$suggestions = _ref2.suggestions,
      suggestions = _ref2$suggestions === void 0 ? [] : _ref2$suggestions,
      renderSuggestion = _ref2.renderSuggestion,
      onHighlight = _ref2.onHighlight,
      onSelect = _ref2.onSelect,
      className = _ref2.className,
      listItemClassName = _ref2.listItemClassName,
      query = _ref2.query,
      _ref2$enableKeyboardI = _ref2.enableKeyboardInteraction,
      enableKeyboardInteraction = _ref2$enableKeyboardI === void 0 ? true : _ref2$enableKeyboardI,
      hasSections = _ref2.hasSections,
      rest = _objectWithoutProperties(_ref2, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var _useState = useState(-1),
      _useState2 = _slicedToArray(_useState, 2),
      highlightedIndex = _useState2[0],
      setHighlightedIndex = _useState2[1];

  var highlightedIndexRef = useRef(-1);
  var listRef = useRef([]);

  var _suggestions = normalizeSuggestions(suggestions, hasSections);

  var handleHighlight = function (evt, index) {
    setHighlightedIndex(index);
    highlightedIndexRef.current = index;
    /* istanbul ignore next */

    if (index !== -1) {
      var id = _suggestions[index].section ? "".concat(_suggestions[index].section.id, "_").concat(_suggestions[index].value.id) : _suggestions[index].value.id;
      var highlightedElm = listRef.current[id];

      if (highlightedElm && highlightedElm.scrollIntoView) {
        highlightedElm.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
    /* istanbul ignore next */


    if (onHighlight) {
      onHighlight(evt, _suggestions[index].value, _suggestions[index].section);
    }
  };

  var handleSelect = function (evt, index) {
    /* istanbul ignore next */
    if (onSelect) {
      onSelect(evt, _suggestions[index].value, _suggestions[index].section);
    }
  };
  /* istanbul ignore next */


  var handleKeyDown = function (evt) {
    if (!_suggestions.length) {
      return;
    }

    var currentHighlightedIndex = highlightedIndexRef.current;
    /* istanbul ignore next */

    var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

    if (KeyCode.IS_ARROW_UP) {
      evt.preventDefault();

      if (currentHighlightedIndex > 0) {
        handleHighlight(evt, currentHighlightedIndex - 1);
      } else if (currentHighlightedIndex === 0) {
        handleHighlight(evt, _suggestions.length - 1);
      }
    } else if (KeyCode.IS_ARROW_DOWN) {
      evt.preventDefault();

      if (currentHighlightedIndex < _suggestions.length - 1) {
        handleHighlight(evt, currentHighlightedIndex + 1);
      } else if (currentHighlightedIndex === _suggestions.length - 1) {
        handleHighlight(evt, 0);
      }
    } else if (KeyCode.IS_ENTER) {
      evt.preventDefault();

      if (currentHighlightedIndex >= 0) {
        handleSelect(evt, currentHighlightedIndex);
      }
    }
  };

  useEffect(function () {
    setHighlightedIndex(-1);
    highlightedIndexRef.current = -1;

    if (suggestions.length && enableKeyboardInteraction) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [suggestions, enableKeyboardInteraction]);

  var renderSuggestionList = function (items, section) {
    var normalizedIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    return items.map(function (suggestion, index) {
      var cssVal = [_styles.suggestion, highlightedIndex >= 0 && suggestion === _suggestions[highlightedIndex].value && _styles.highlightedSuggesiton];
      var id = section ? "".concat(section.id, "_").concat(suggestion.id) : suggestion.id;
      var suggestionContent = renderSuggestion ? renderSuggestion(suggestion, query, section) : defaultRenderSuggestion(suggestion, query, _styles);

      var handleInteraction = function (evt) {
        var interactionIndex = normalizedIndex + index;
        if (evt.type === 'click') handleSelect(evt, interactionIndex);
        if (evt.type === 'mousemove') handleHighlight(evt, interactionIndex);
      };

      return jsx("li", _extends({
        role: "option"
      }, section ? {
        'aria-describedby': section.id
      } : null, {
        key: id,
        id: id,
        ref: function ref(el) {
          return listRef.current[id] = el;
        },
        css: cssVal,
        className: listItemClassName,
        onMouseDown: function onMouseDown(evt) {
          return evt.preventDefault();
        },
        onMouseMove: handleInteraction,
        onClick: handleInteraction,
        "data-ppui": "true"
      }), suggestionContent);
    });
  };

  var listItems = hasSections ? function renderSections() {
    var currentNormalizedIndex = 0;
    return suggestions.reduce(function (acc, section) {
      if (section.suggestions && section.suggestions.length) {
        acc.push(jsx("li", {
          role: "presentation",
          key: section.id,
          "data-ppui": "true"
        }, jsx("div", {
          css: _styles.content,
          id: section.id,
          "aria-hidden": "true",
          "data-ppui": "true"
        }, jsx(CaptionText, {
          "data-ppui": "true"
        }, section.title)), jsx("ul", {
          role: "listbox",
          css: _styles.suggestionSublist,
          "aria-labelledby": section.id,
          "data-ppui": "true"
        }, renderSuggestionList(section.suggestions, section, currentNormalizedIndex))));
        currentNormalizedIndex += section.suggestions.length;
      }

      return acc;
    }, []);
  }() : renderSuggestionList(suggestions);

  if (_suggestions.length) {
    return jsx("ul", _extends({
      role: "listbox",
      css: _styles.suggestionList,
      className: className
    }, rest, {
      "data-ppui": "true"
    }), listItems);
  }

  return null;
};

var _excluded$1 = ["id", "value", "debounceTimeout", "fetchSuggestions", "suggestionList", "isSuggestionListInline", "isLoadingSuggestions", "onChange", "onFocus", "onKeyDown", "onBlur", "onClear", "className", "triggerComponent", "suggestions", "defaultSuggestionList", "suggestionListFooter", "suggestionListContainerClassName", "suggestionListFooterClassName", "suggestionListChangeMessage", "suggestionListLoadingMessage", "suggestionListErrorMessage", "onSuggestionListClose"];
var DEBOUNCE_TIMEOUT = 500;
/* Get total number of suggestions */

var getNormalizedSuggestionLength = function (suggestions, hasSections) {
  if (!hasSections) {
    return suggestions.length;
  }

  return suggestions.reduce(function (acc, section) {
    if (section.suggestions) {
      acc += section.suggestions.length;
    }

    return acc;
  }, 0);
};
/* eslint-disable complexity */


var AutoSuggest = forwardRef(function (props, ref) {
  var _props$id = props.id,
      id = _props$id === void 0 ? 'default' : _props$id,
      value = props.value,
      _props$debounceTimeou = props.debounceTimeout,
      debounceTimeout = _props$debounceTimeou === void 0 ? DEBOUNCE_TIMEOUT : _props$debounceTimeou,
      _props$fetchSuggestio = props.fetchSuggestions,
      fetchSuggestions = _props$fetchSuggestio === void 0 ? function () {
    return devWarning('Required prop `fetchSuggestions` is missing');
  } : _props$fetchSuggestio,
      _props$suggestionList = props.suggestionList,
      suggestionList = _props$suggestionList === void 0 ? jsx(SuggestionList, {
    suggestions: [],
    "data-ppui": "true"
  }) : _props$suggestionList,
      isSuggestionListInline = props.isSuggestionListInline,
      isLoadingSuggestions = props.isLoadingSuggestions,
      onChange = props.onChange,
      onFocus = props.onFocus,
      onKeyDown = props.onKeyDown,
      onBlur = props.onBlur,
      onClear = props.onClear,
      className = props.className,
      _props$triggerCompone = props.triggerComponent,
      triggerComponent = _props$triggerCompone === void 0 ? TextInput : _props$triggerCompone,
      _props$suggestions = props.suggestions,
      _props$defaultSuggest = props.defaultSuggestionList,
      defaultSuggestionList = _props$defaultSuggest === void 0 ? jsx(SuggestionList, {
    suggestions: [],
    "data-ppui": "true"
  }) : _props$defaultSuggest,
      suggestionListFooter = props.suggestionListFooter,
      suggestionListContainerClassName = props.suggestionListContainerClassName,
      suggestionListFooterClassName = props.suggestionListFooterClassName,
      suggestionListChangeMessage = props.suggestionListChangeMessage,
      _props$suggestionList2 = props.suggestionListLoadingMessage,
      suggestionListLoadingMessage = _props$suggestionList2 === void 0 ? 'Loading suggestions' : _props$suggestionList2,
      suggestionListErrorMessage = props.suggestionListErrorMessage,
      onSuggestionListClose = props.onSuggestionListClose,
      rest = _objectWithoutProperties(props, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var timerRef = useRef();
  var internalRef = useRef();
  var suggestionListContainerRef = useRef();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      areSuggestionsVisible = _useState2[0],
      setSuggestionsVisible = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      highlightedSuggestionId = _useState4[0],
      setHighlightedSuggestionId = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isTriggerFocused = _useState6[0],
      setTriggerFocus = _useState6[1];

  var inputRef = ref || internalRef;
  var suggestionsLength = getNormalizedSuggestionLength(suggestionList.props.suggestions || [], suggestionList.props.hasSections);
  /* istanbul ignore next */

  var focusSuggestionLength = getNormalizedSuggestionLength(defaultSuggestionList.props.suggestions || [], defaultSuggestionList.props.hasSections);

  var handleOutsideClick = function (evt) {
    var inputElm = inputRef.current;
    var suggestionListContainer = suggestionListContainerRef.current;
    /* istanbul ignore next */

    if (evt.target === inputElm || suggestionListContainer && suggestionListContainer.contains(evt.target)) {
      return;
    }
    /* istanbul ignore next */


    setSuggestionsVisible(false);
  };

  useEffect(function () {
    return function () {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  useEffect(function () {
    if (areSuggestionsVisible && !isSuggestionListInline) {
      document.addEventListener('click', handleOutsideClick);
    }

    return function () {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [areSuggestionsVisible, isSuggestionListInline]);

  var callSuggestionListClose = function (evt) {
    if (onSuggestionListClose && suggestionsLength) {
      onSuggestionListClose(evt);
    }
  };

  var handleHighlight = function (evt, suggestion, section) {
    setHighlightedSuggestionId(section ? "".concat(section.id, "_").concat(suggestion.id) : suggestion.id);
    /* istanbul ignore next */

    if (suggestionList.props.onHighlight) {
      suggestionList.props.onHighlight(evt, suggestion, section);
    }
  };

  var handleSelect = function (evt, suggestion, section) {
    /* istanbul ignore next */
    if (suggestionList.props.onSelect) {
      suggestionList.props.onSelect(evt, suggestion, section);
    }

    if (!isSuggestionListInline) {
      setSuggestionsVisible(false);
    }
  };

  var suggestionListId = "".concat(id, "-suggestionList");
  var footer = suggestionListFooter ? jsx("div", {
    css: _styles.content,
    className: suggestionListFooterClassName,
    "data-ppui": "true"
  }, suggestionListFooter) : null;
  var suggestionsElm = !isLoadingSuggestions && suggestionsLength > 0 && jsx(React.Fragment, null, React.cloneElement(suggestionList, {
    id: suggestionListId,
    'aria-labelledby': "".concat(id, "-label"),
    onHighlight: handleHighlight,
    onSelect: handleSelect,
    enableKeyboardInteraction: isTriggerFocused
  }), footer);
  var focusSuggestionElm = focusSuggestionLength > 0 && jsx(React.Fragment, null, React.cloneElement(defaultSuggestionList, {
    id: suggestionListId,
    'aria-labelledby': "".concat(id, "-label"),
    onHighlight: handleHighlight,
    onSelect: handleSelect,
    enableKeyboardInteraction: isTriggerFocused
  }), footer);
  var loadingElm = isLoadingSuggestions ? jsx("div", {
    css: _styles.centeredContent,
    "data-ppui": "true"
  }, jsx(LoadingSpinner, {
    screenReaderText: suggestionListLoadingMessage,
    size: "md",
    "data-ppui": "true"
  })) : null;
  var errorElm = suggestionListErrorMessage && !suggestionsLength && !isLoadingSuggestions ? jsx(ContextualAlert, {
    css: _styles.errorMessage,
    type: "warning",
    "data-ppui": "true"
  }, suggestionListErrorMessage) : null;
  var suggestionListContainerCssVal = isSuggestionListInline ? _styles.suggestionListContainer : _styles.elevatedSuggestionListContainer;
  var suggestionContainerElm = null;

  if (value && areSuggestionsVisible && (suggestionsElm || loadingElm || errorElm)) {
    suggestionContainerElm = jsx("div", {
      css: suggestionListContainerCssVal,
      className: suggestionListContainerClassName,
      ref: suggestionListContainerRef,
      "data-ppui": "true"
    }, suggestionsElm, loadingElm, errorElm);
  } else if (!value && areSuggestionsVisible && focusSuggestionLength > 0) {
    suggestionContainerElm = jsx("div", {
      css: suggestionListContainerCssVal,
      className: suggestionListContainerClassName,
      ref: suggestionListContainerRef,
      "data-ppui": "true"
    }, focusSuggestionElm);
  }

  var _suggestListChangeMessage = suggestionListChangeMessage || "".concat(suggestionsLength, " suggestions available");

  var otherProps = triggerComponent !== TextInput ? {
    onClear: function handleClear(evt) {
      callSuggestionListClose(evt);

      if (onClear) {
        onClear(evt);
      }
    }
  } : {};
  return jsx("div", {
    css: _styles.base,
    className: className,
    "data-ppui-info": "auto-suggest_2.9.6"
  }, jsx(triggerComponent, _extends({}, rest, {
    id: id,
    value: value,
    ref: inputRef,
    onChange: function handleChange(evt) {
      var newValue = evt.target.value;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      /* istanbul ignore next */


      timerRef.current = setTimeout(function () {
        fetchSuggestions(newValue);
      }, debounceTimeout);
      setSuggestionsVisible(true);
      onChange(evt);
    },
    onFocus: function handleFocus(evt) {
      if (suggestionsLength || focusSuggestionLength) {
        setSuggestionsVisible(true);
      }

      fetchSuggestions(value);
      setTriggerFocus(true);
      /* istanbul ignore next */

      if (onFocus) {
        onFocus(evt);
      }
    },
    onKeyDown: function handleKey(evt) {
      /* istanbul ignore next */
      var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

      if (KeyCode.IS_ESC || KeyCode.IS_TAB) {
        if (!isSuggestionListInline) {
          setSuggestionsVisible(false);
        }
        /* istanbul ignore next */


        if (KeyCode.IS_ESC && onClear) {
          onClear(evt);
        }
      }
      /* istanbul ignore next */


      if (onKeyDown) {
        onKeyDown(evt);
      }
    },
    onBlur: function handleBlur(evt) {
      // onSuggestionListClose callback
      // this callback is triggered ONLY when suggestionList is visible
      callSuggestionListClose(evt);
      setTriggerFocus(false);
      /* istanbul ignore next */

      if (onBlur) {
        onBlur(evt);
      }
    },
    "aria-activedescendant": highlightedSuggestionId,
    "aria-autocomplete": "list",
    role: "combobox",
    "aria-expanded": suggestionsLength > 0 && areSuggestionsVisible,
    "aria-owns": suggestionListId,
    "aria-haspopup": "listbox"
  }, otherProps, {
    "data-ppui": "true"
  })), suggestionContainerElm, jsx(ScreenReaderContent, {
    role: "status",
    "data-ppui": "true"
  }, _suggestListChangeMessage));
});

export { AutoSuggest, SuggestionList, styles as autoSuggestStyles };
